import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import {
    log as logAutoApplyFeedback,
    Category,
    AvroOptional,
} from '@indeed/signals-schema/com/indeed/signals/schema/jspremium/AutoApplyFeedback';
import { useFeedbackCategories } from './ReportIssueContext';
import { IssueCategory } from './types';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Heading,
    Radio,
    Text,
    TextAreaFormField,
    TextLink,
} from '@indeed/ifl-components';
import { MarkupComponents } from '@indeed/frontend-markup-react-components';
import PageTransition from '../../../components/PageTransition/PageTransition';
import useGettext from '../../../util/GettextFactory';
import { useGetJobSeekerAccountKeyQuery } from './__generated__/DetailsFormStep.generated';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { NewWindow } from '@indeed/ifl-icons';

const MAX_CHARACTERS = 300;

/**
 * Details form step for the Report Issue flow.
 * Handles form logic, submission, and signals logging.
 * Note: Category validation is handled by ValidCategoryGuard at the route level.
 */
const DetailsFormStep: React.FC = () => {
    const { category } = useParams<{ category: string }>();
    const [searchParams] = useSearchParams();
    const { data: accountData, loading } = useGetJobSeekerAccountKeyQuery();
    const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const gt = useGettext();
    const feedbackCategories = useFeedbackCategories();
    const { indeedBaseUrl } = useInitialState();

    // Extract accountKey from query response
    const accountKey = accountData?.jobSeekerProfile?.profile?.accountKey;

    // Category is guaranteed to be valid by ValidCategoryGuard
    const selectedCategory = category as IssueCategory;

    // Find the selected category data
    const selectedCategoryData = useMemo(() => {
        return feedbackCategories.find((cat) => cat.id === selectedCategory);
    }, [feedbackCategories, selectedCategory]);

    // Handle issue selection (supports both single and multi-select)
    const handleIssueSelect = (value: string, checked?: boolean) => {
        if (selectedCategoryData?.allowMultiple && checked !== undefined) {
            // Multi-select mode (Checkbox)
            setSelectedIssues((prev) => {
                if (checked) {
                    return [...prev, value];
                } else {
                    return prev.filter((v) => v !== value);
                }
            });
        } else {
            // Single-select mode (Radio)
            setSelectedIssues([value]);
        }
    };

    const handleSubmit = async () => {
        // Get jobKey and encryptedApplyId from query parameters
        const jobKey = searchParams.get('jobKey');
        const encryptedApplyId = searchParams.get('encryptedApplyId');

        // Map IssueCategory to Category enum
        const categoryEnum = Category[selectedCategory];

        // Log feedback using signals schema with accountKey from GraphQL
        logAutoApplyFeedback({
            accountKey: AvroOptional.string(accountKey),
            jobKey: AvroOptional.string(jobKey),
            encryptedApplyId: AvroOptional.string(encryptedApplyId),
            category: { Category: categoryEnum },
            answerFields: AvroOptional.array(selectedIssues),
            details: AvroOptional.string(additionalDetails),
        });

        await JSMABridge.dismissModalWithWebView({});
    };

    const isMultiSelect = selectedCategoryData?.allowMultiple || false;
    const heading = selectedCategoryData?.heading || gt.gettext('Please tell us what happened');
    const issueOptions = selectedCategoryData?.options || [];
    const shouldShowOptions = selectedCategory !== 'OTHER';
    const characterCount = additionalDetails.length;

    // Form is complete when:
    // - OTHER category: additional details are provided
    // - Other categories: at least one option is selected
    const isFormComplete =
        selectedCategory === 'OTHER'
            ? additionalDetails.trim().length > 0
            : selectedIssues.length > 0;

    return (
        <PageTransition pageKey="auto-apply-report-issue-details">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    {/* Title and Subtitle */}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Heading level={4} sx={{ marginBlockEnd: 1 }}>
                            {heading}
                        </Heading>
                        <Text level={2} variant={'secondary'}>
                            {gt.gettext("Your feedback won't be shared with the employer.")}
                        </Text>
                    </Box>

                    {/* Radio or Checkbox Options - Skip for 'other' */}
                    {shouldShowOptions && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}
                        >
                            {issueOptions.map((option) =>
                                isMultiSelect ? (
                                    <Checkbox
                                        key={option}
                                        name="issue-option"
                                        value={option}
                                        checked={selectedIssues.includes(option)}
                                        label={option}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleIssueSelect(option, e.target.checked)
                                        }
                                        sx={{ paddingBlockStart: 0, paddingBlockEnd: 0 }}
                                    />
                                ) : (
                                    <Radio
                                        key={option}
                                        name="issue-option"
                                        value={option}
                                        checked={selectedIssues.includes(option)}
                                        label={option}
                                        onChange={() => handleIssueSelect(option)}
                                        sx={{ paddingBlockStart: 0, paddingBlockEnd: 0 }}
                                    />
                                ),
                            )}
                        </Box>
                    )}

                    {/* Additional Details - Show when option selected OR for 'other' */}
                    {(selectedIssues.length > 0 || selectedCategory === 'OTHER') && (
                        <>
                            {/* For OTHER category, show alert first */}
                            {selectedCategory === 'OTHER' && (
                                <Alert>
                                    {gt.gettext(
                                        'Do not include any sensitive information such as phone numbers or email addresses.',
                                    )}
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {selectedCategory !== 'OTHER' && (
                                    <Text level={3} variant={'contrast'} sx={{ fontWeight: 700 }}>
                                        {gt.gettext('Additional details')}
                                    </Text>
                                )}
                                <TextAreaFormField
                                    textareaProps={{
                                        value: additionalDetails,
                                        onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                            if (e.target.value.length <= MAX_CHARACTERS) {
                                                setAdditionalDetails(e.target.value);
                                            }
                                        },
                                        rows: 5,
                                        'aria-label': gt.gettext('Details'),
                                    }}
                                />
                                <Text level={3} variant={'secondary'}>
                                    {characterCount}/{MAX_CHARACTERS}
                                </Text>
                            </Box>

                            {/* For non-OTHER categories, show alert after textarea */}
                            {selectedCategory !== 'OTHER' && (
                                <Alert>
                                    {gt.gettext(
                                        'Do not include any sensitive information such as phone numbers or email addresses.',
                                    )}
                                </Alert>
                            )}
                        </>
                    )}

                    {/* Submit Button - Always visible but disabled until form is complete */}
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={!isFormComplete || loading}
                    >
                        {gt.gettext('Submit feedback')}
                    </Button>

                    {/* Terms Text */}
                    {selectedCategory === 'JOB' && (
                        <Text level={1}>
                            <MarkupComponents
                                components={{
                                    termsOfServiceLink: (children) => (
                                        <TextLink
                                            href={`${indeedBaseUrl}/legal/terms-of-service`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            iconAfter={<NewWindow />}
                                        >
                                            {children}
                                        </TextLink>
                                    ),
                                }}
                            >
                                {gt.pgettext(
                                    'Terms of Service text',
                                    "All Job Ads are subject to Indeed's [{0}|termsOfServiceLink]. " +
                                        'We allow users to flag postings that may be in violation of those terms. ' +
                                        'Job Ads may also be flagged by Indeed. However, no moderation system is perfect, ' +
                                        'and flagging a posting does not ensure that it will be removed.',
                                    ['Terms of Service'],
                                )}
                            </MarkupComponents>
                        </Text>
                    )}
                </Box>
            </Box>
        </PageTransition>
    );
};

export default DetailsFormStep;
